import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost, xpToLevel } from '../theme'
import { CHAPTERS } from '../data/chapters'
import { supabase } from '../supabaseClient'

function Sidebar({ currentView, onNavigate, state, speech, isMobile = false, isOpen = false, session = null, onSignOut = null }) {
  const { completedChapters, xpTotal } = state;
  const { level } = xpToLevel(xpTotal);
  const doneSet = new Set(completedChapters);
  const ch0ChecklistCount = Object.values(state.chapter0Checklist || {}).filter(Boolean).length;
  const ch0Complete = doneSet.has('ch00') || ch0ChecklistCount >= 13;
  const ch01Locked = !ch0Complete && !doneSet.has('ch01');

  const [openSections, setOpenSections] = useState({ lessons: true, practice: false, track: false, calculators: false, innerwork: false, today: true });
  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const isInnerWorkView = ['innerwork','innerwork_pretrade','innerwork_reflect','innerwork_mirror','innerwork_resources'].includes(currentView);

  const navItem = (view, label, icon, indent = false) => {
    const active = currentView === view;
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 12px 7px ' + (indent ? '22px' : '12px'), borderRadius: '7px', cursor: 'pointer', background: active ? 'rgba(201,168,76,0.1)' : 'transparent', border: `0.5px solid ${active ? 'rgba(201,168,76,0.3)' : 'transparent'}`, marginBottom: '1px' }}
        onClick={() => onNavigate(view)}>
        <span style={{ fontSize: '13px', color: active ? V.goldL : V.muted }}>{icon}</span>
        <span style={{ fontSize: '12px', color: active ? V.text : V.muted, fontWeight: active ? 500 : 400 }}>{label}</span>
      </div>
    );
  };

  const sectionHeader = (key, emoji, label) => {
    const open = openSections[key];
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', cursor: 'pointer', borderRadius: '7px', background: V.bg3, borderLeft: `3px solid ${open ? V.gold : 'transparent'}`, marginBottom: open ? '4px' : '6px', marginTop: '6px' }}
        onClick={() => toggleSection(key)}>
        <span style={{ fontSize: '13px' }}>{emoji}</span>
        <span style={{ ...serif, fontSize: '15px', color: open ? V.goldL : V.muted, fontWeight: 500, flex: 1 }}>{label}</span>
        <span style={{ fontSize: '10px', color: V.dim }}>{open ? '▾' : '▸'}</span>
      </div>
    );
  };

  const subLabel = (text) => (
    <div style={{ fontSize: '9px', letterSpacing: '0.14em', textTransform: 'uppercase', color: V.dim, padding: '10px 12px 3px', fontFamily: "'Jost',sans-serif" }}>{text}</div>
  );

  return (
    <div style={{ width: '260px', flexShrink: 0, background: V.bg2, borderRight: `0.5px solid ${V.border}`, display: 'flex', flexDirection: 'column', padding: '20px 10px', overflowY: 'auto', overflowX: 'hidden', ...(isMobile ? { position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 1000, transform: isOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.25s ease', boxShadow: isOpen ? '4px 0 24px rgba(0,0,0,0.5)' : 'none' } : { position: 'sticky', top: 0, height: '100vh', zIndex: 10, alignSelf: 'flex-start' }) }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: `0.5px solid ${V.border}` }}>
        <div style={{ fontSize: '22px', color: V.gold, marginBottom: '4px' }}>♀</div>
        <div style={{ ...serif, fontSize: '14px', fontWeight: 500, color: V.goldL, lineHeight: 1.2 }}>Trade Like a Woman</div>
        <div style={{ fontSize: '10px', color: V.muted, letterSpacing: '0.08em', marginTop: '2px' }}>THE FOUNDATION</div>
        <div style={{ fontSize: '10px', color: V.gold, marginTop: '8px' }}>Lv.{level} · {xpTotal.toLocaleString()} XP</div>
      </div>

      {/* ── SECTION 1: LESSONS ── */}
      {sectionHeader('lessons', '📚', 'Lessons')}
      {openSections.lessons && (
        <>
          {navItem('dashboard', 'Dashboard', '⌂')}
          {navItem('intro', "Lynn's Letter", '♀')}
          {/* Ch.0 — special nav with checklist state */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 12px 6px 22px', borderRadius: '6px', cursor: 'pointer', background: currentView === 'chapter_ch00' ? 'rgba(201,168,76,0.08)' : 'transparent', marginBottom: '1px' }}
            onClick={() => onNavigate('chapter_ch00')}>
            <span style={{ fontSize: '9px', color: doneSet.has('ch00') ? V.sage : currentView === 'chapter_ch00' ? V.gold : V.dim }}>{doneSet.has('ch00') ? '✓' : currentView === 'chapter_ch00' ? '▶' : '○'}</span>
            <span style={{ fontSize: '11px', color: currentView === 'chapter_ch00' ? V.text : V.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Ch.0 Setting Up Your Platform</span>
          </div>
          {/* Chapters 1–10 */}
          {CHAPTERS.map(ch => {
            const done = doneSet.has(ch.id);
            const active = currentView === 'chapter_' + ch.id;
            const locked = ch.id === 'ch01' && ch01Locked;
            return (
              <div key={ch.id}
                style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 12px 6px 22px', borderRadius: '6px', cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.38 : 1, background: active ? 'rgba(201,168,76,0.08)' : 'transparent', marginBottom: '1px' }}
                onClick={() => !locked && onNavigate('chapter_' + ch.id)}
                title={locked ? 'Complete Chapter 0 checklist to unlock' : undefined}>
                <span style={{ fontSize: '9px', color: done ? V.sage : active ? V.gold : V.dim }}>{done ? '✓' : locked ? '🔒' : active ? '▶' : '○'}</span>
                <span style={{ fontSize: '11px', color: active ? V.text : V.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Ch.{ch.num} {ch.name}</span>
              </div>
            );
          })}
          {subLabel('── Indicators ──')}
          {navItem('lesson_candlesticks', 'Candlesticks — Lesson',    '◐', true)}
          {navItem('lesson_ma',           'Moving Averages — Lesson', '∿', true)}
          {navItem('lesson_rsi',          'RSI — Lesson',             '○', true)}
          {navItem('lesson_greeks',       'Greeks — Reference',      'Δ', true)}
          {subLabel(`── Advanced Concepts ${state.tier2Unlocked ? '' : '🔒'} ──`)}
          {(() => {
            const locked = !state.tier2Unlocked;
            const activeAdv = currentView === 'adv_reading';
            const activeVwap = currentView === 'lesson_vwap';
            const activeBB = currentView === 'lesson_bb';
            const activePM = currentView === 'portfolio_management';
            return (
              <>
                <div
                  title={locked ? 'Unlocks with Tier 2 access from your mentor' : undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 12px 7px 22px', borderRadius: '7px', cursor: 'pointer', background: activeAdv ? 'rgba(201,168,76,0.1)' : 'transparent', border: `0.5px solid ${activeAdv ? 'rgba(201,168,76,0.3)' : 'transparent'}`, marginBottom: '1px', opacity: locked ? 0.6 : 1 }}
                  onClick={() => onNavigate('adv_reading')}>
                  <span style={{ fontSize: '13px', color: activeAdv ? V.goldL : V.muted }}>{locked ? '🔒' : '◈'}</span>
                  <span style={{ fontSize: '12px', color: activeAdv ? V.text : V.muted, fontWeight: activeAdv ? 500 : 400 }}>Market Analysis {locked ? '🔒' : ''}</span>
                </div>
                <div
                  title={locked ? 'Unlocks with Tier 2 access from your mentor' : undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 12px 7px 22px', borderRadius: '7px', cursor: 'pointer', background: activeVwap ? 'rgba(201,168,76,0.1)' : 'transparent', border: `0.5px solid ${activeVwap ? 'rgba(201,168,76,0.3)' : 'transparent'}`, marginBottom: '1px', opacity: locked ? 0.6 : 1 }}
                  onClick={() => onNavigate('lesson_vwap')}>
                  <span style={{ fontSize: '13px', color: activeVwap ? V.goldL : V.muted }}>{locked ? '🔒' : '◈'}</span>
                  <span style={{ fontSize: '12px', color: activeVwap ? V.text : V.muted, fontWeight: activeVwap ? 500 : 400 }}>VWAP — Lesson {locked ? '🔒' : ''}</span>
                </div>
                <div
                  title={locked ? 'Unlocks with Tier 2 access from your mentor' : undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 12px 7px 22px', borderRadius: '7px', cursor: 'pointer', background: activeBB ? 'rgba(201,168,76,0.1)' : 'transparent', border: `0.5px solid ${activeBB ? 'rgba(201,168,76,0.3)' : 'transparent'}`, marginBottom: '1px', opacity: locked ? 0.6 : 1 }}
                  onClick={() => onNavigate('lesson_bb')}>
                  <span style={{ fontSize: '13px', color: activeBB ? V.goldL : V.muted }}>{locked ? '🔒' : '◎'}</span>
                  <span style={{ fontSize: '12px', color: activeBB ? V.text : V.muted, fontWeight: activeBB ? 500 : 400 }}>Bollinger Bands — Lesson {locked ? '🔒' : ''}</span>
                </div>
                <div
                  title={locked ? 'Unlocks with Tier 2 access from your mentor' : undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 12px 7px 22px', borderRadius: '7px', cursor: 'pointer', background: activePM ? 'rgba(201,168,76,0.1)' : 'transparent', border: `0.5px solid ${activePM ? 'rgba(201,168,76,0.3)' : 'transparent'}`, marginBottom: '1px', opacity: locked ? 0.6 : 1 }}
                  onClick={() => onNavigate('portfolio_management')}>
                  <span style={{ fontSize: '13px', color: activePM ? V.goldL : V.muted }}>{locked ? '🔒' : '◈'}</span>
                  <span style={{ fontSize: '12px', color: activePM ? V.text : V.muted, fontWeight: activePM ? 500 : 400 }}>Portfolio Management {locked ? '🔒' : ''}</span>
                </div>
              </>
            );
          })()}
          {navItem('tradingrules', 'Trading Rules',  '📋', true)}
          {navItem('manual',       'Trading Manual', '◉',  true)}
          {navItem('glossary',     'Glossary',       '◇',  true)}
        </>
      )}

      {/* ── SECTION 2: PRACTICE ── */}
      {sectionHeader('practice', '🎯', 'Practice')}
      {openSections.practice && (
        <>
          <div style={{ fontSize: '9px', letterSpacing: '0.12em', color: V.dim, textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", padding: '6px 12px 4px 12px', marginTop: '2px' }}>── TIER 1 PRACTICE ──</div>
          {navItem('full_trade_simulator', 'The Full Trade Simulator',  '📊', true)}
          {navItem('tradebuilder',       'Trade Builder',             '▶',  true)}
          {navItem('spread_builder_practice','CSP & Spread Simulator',         '◈',  true)}
          {navItem('roll_calculator',        'Roll vs Close Calculator',      '⟳',  true)}
          {navItem('ma',                 'MA Bands Simulator',        '∿',  true)}
          {navItem('ind_candles_practice', 'Candlesticks — Practice',   '◐',  true)}
          {navItem('support_resistance_practice', 'Support & Resistance — Practice', '◈', true)}
          {navItem('confluence_practice', 'Confluence — Practice', '◈', true)}
          <div style={{ fontSize:'10px', color:V.muted, fontStyle:'italic', fontFamily:"'Jost',sans-serif", paddingLeft:'36px', marginTop:'-8px', marginBottom:'4px' }}>When everything points to the same answer</div>
          {navItem('practice_ma',        'Moving Averages — Practice','∿',  true)}
          {navItem('practice_rsi',       'RSI — Practice',            '○',  true)}
          {navItem('practice_greeks',    'Practice the Greeks',       '△',  true)}
          {navItem('ind_greeks_practice','The Greeks — Quiz',          'Δ',  true)}
          {navItem('tradesetup',         'Trade Setup Simulator',     '◬',  true)}
          {navItem('stockselection',     'Stock Selection',           '◎',  true)}
          {navItem('scenarios',          'Scenario Library',          '?',  true)}
          {navItem('test',               'Readiness Test',            '◎',  true)}
          <div style={{ fontSize: '9px', letterSpacing: '0.12em', color: V.dim, textTransform: 'uppercase', fontFamily: "'Jost',sans-serif", padding: '8px 12px 4px 12px' }}>── TIER 2 PRACTICE {state.tier2Unlocked ? '' : '🔒'} ──</div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 12px 6px 22px', borderRadius: '6px', cursor: 'pointer', background: currentView === 'spx_confluence' ? 'rgba(201,168,76,0.08)' : 'transparent', marginBottom: '1px', opacity: state.tier2Unlocked ? 1 : 0.6 }}
            onClick={() => onNavigate('spx_confluence')}>
            <span style={{ fontSize: '13px', color: currentView === 'spx_confluence' ? V.goldL : V.muted }}>{state.tier2Unlocked ? '◈' : '🔒'}</span>
            <span style={{ fontSize: '12px', color: currentView === 'spx_confluence' ? V.text : V.muted, fontWeight: currentView === 'spx_confluence' ? 500 : 400, whiteSpace: 'nowrap' }}>SPX Confluence{state.tier2Unlocked ? '' : ' 🔒'}</span>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 12px 6px 22px', borderRadius: '6px', cursor: 'pointer', background: currentView === 'practice_vwap' ? 'rgba(201,168,76,0.08)' : 'transparent', marginBottom: '1px', opacity: state.tier2Unlocked ? 1 : 0.6 }}
            onClick={() => onNavigate('practice_vwap')}>
            <span style={{ fontSize: '13px', color: currentView === 'practice_vwap' ? V.goldL : V.muted }}>{state.tier2Unlocked ? '◈' : '🔒'}</span>
            <span style={{ fontSize: '12px', color: currentView === 'practice_vwap' ? V.text : V.muted, fontWeight: currentView === 'practice_vwap' ? 500 : 400, whiteSpace: 'nowrap' }}>VWAP — Practice{state.tier2Unlocked ? '' : ' 🔒'}</span>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 12px 6px 22px', borderRadius: '6px', cursor: 'pointer', background: currentView === 'ind_bb_practice' ? 'rgba(201,168,76,0.08)' : 'transparent', marginBottom: '1px', opacity: state.tier2Unlocked ? 1 : 0.6 }}
            onClick={() => onNavigate('ind_bb_practice')}>
            <span style={{ fontSize: '13px', color: currentView === 'ind_bb_practice' ? V.goldL : V.muted }}>{state.tier2Unlocked ? '◎' : '🔒'}</span>
            <span style={{ fontSize: '12px', color: currentView === 'ind_bb_practice' ? V.text : V.muted, fontWeight: currentView === 'ind_bb_practice' ? 500 : 400, whiteSpace: 'nowrap' }}>Bollinger Bands — Practice{state.tier2Unlocked ? '' : ' 🔒'}</span>
          </div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 12px 6px 22px', borderRadius: '6px', cursor: 'pointer', background: currentView === '0dte_sim' ? 'rgba(201,168,76,0.08)' : 'transparent', marginBottom: '1px' }}
            onClick={() => onNavigate('0dte_sim')}>
            <span style={{ fontSize: '13px', color: currentView === '0dte_sim' ? V.goldL : V.muted }}>{state.tier2Unlocked ? '⚡' : '🔒'}</span>
            <span style={{ fontSize: '12px', color: currentView === '0dte_sim' ? V.text : V.muted, fontWeight: currentView === '0dte_sim' ? 500 : 400, whiteSpace: 'nowrap' }}>0DTE Simulator{state.tier2Unlocked ? '' : ' 🔒'}</span>
          </div>
        </>
      )}

      {/* ── SECTION 3: TRACK ── */}
      {sectionHeader('track', '📊', 'Track')}
      {openSections.track && (
        <>
          {navItem('dashboard',      'My Progress',    '⌂')}
          {navItem('risk_dashboard', 'Risk Dashboard', '◈')}
          {navItem('pnl',            'Profit & Loss',  '📈')}
          {navItem('journal',   'Trading Journal', '✎')}
          {navItem('taxhub',    'Tax Hub',         '◇')}
        </>
      )}

      {/* ── SECTION 4: CALCULATORS ── */}
      {sectionHeader('calculators', '💰', 'Calculators')}
      {openSections.calculators && (
        <>
          {subLabel('── Trading ──')}
          {navItem('calc_position',  'Position Size',   '◎', true)}
          {navItem('calc_options',   'Options Profit',  '◱', true)}
          {navItem('calc_breakeven', 'Break Even',      '◬', true)}
          {navItem('calc_rr',        'Risk / Reward',   '◈', true)}
          {navItem('calc_compound',  'Compound Growth', '○', true)}
          {subLabel(`── Financial Planning ${state.isGraduated ? '' : '🔒'} ──`)}
          {navItem('calc_retirement', `Retirement Planner${state.isGraduated ? '' : ' 🔒'}`,   '◉', true)}
          {navItem('calc_budget',     `Budget & Cash Flow${state.isGraduated ? '' : ' 🔒'}`,   '✎', true)}
          {navItem('calc_rentvbuy',   `Rent vs Buy${state.isGraduated ? '' : ' 🔒'}`,          '⊞', true)}
          {navItem('calc_debt',       `Debt Payoff${state.isGraduated ? '' : ' 🔒'}`,          '◇', true)}
          {navItem('calc_college',    `College Savings${state.isGraduated ? '' : ' 🔒'}`,      '◐', true)}
          {navItem('calc_networth',   `Net Worth${state.isGraduated ? '' : ' 🔒'}`,            '∿', true)}
          {navItem('calc_savings',    `Savings Goal${state.isGraduated ? '' : ' 🔒'}`,         '▶', true)}
          {navItem('calc_car',        `Car vs Invest${state.isGraduated ? '' : ' 🔒'}`,        '◬', true)}
          {subLabel('── Position Management ──')}
          {navItem('roll_calculator', 'Roll vs Close Calculator', '⟳', true)}
        </>
      )}

      {/* ── SECTION 5: INNER WORK ── */}
      {sectionHeader('innerwork', '🌿', 'Inner Work')}
      {openSections.innerwork && (
        <>
          {navItem('innerwork',           'The Inner Work',        '✦')}
          {navItem('meditations',         'Meditations',           '🌿', true)}
          {navItem('innerwork_pretrade',  'Pre-Trade Check-In',    '○',  true)}
          {navItem('innerwork_reflect',   'Post-Trade Reflection', '◎',  true)}
          {navItem('innerwork_mirror',    'The Belief Mirror',     '◈',  true)}
          {navItem('innerwork_resources', 'Resources',             '◇',  true)}
        </>
      )}

      {/* ── SECTION 6: TODAY ── */}
      {sectionHeader('today', '🌅', 'Today')}
      {openSections.today && (
        <>
          {navItem('marketbrief', 'Market Brief',      '◉')}
          {navItem('calendar',    'Economic Calendar', '📅')}
          {navItem('community',   'Community',         '♀')}
          {navItem('first_paper_trade', 'Your First Paper Trade', '♀')}
        </>
      )}

      {/* ── ADMIN TOOLS — only in admin mode or admin email ── */}
      {(state.adminMode || session?.user?.email === 'lynnds5@gmail.com') && (
        <div style={{ marginTop: '4px', padding: '4px 8px' }}>
          <div style={{ fontSize: '9px', color: V.rose, fontFamily: "'Jost',sans-serif", letterSpacing: '0.12em', textTransform: 'uppercase', padding: '4px 8px', marginBottom: '2px' }}>── Admin ──</div>
          {navItem('admin',          'Admin ⚙',            '◈')}
          {navItem('spx_live',       'SPX Live ◈',          '◈', true)}
          {navItem('admin_feedback', 'Feedback Dashboard',   '◎', true)}
        </div>
      )}

      {/* ── SETTINGS — always visible ── */}
      <div
        style={{ marginTop: '8px', borderTop: '1px solid rgba(201,168,76,0.1)', padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
        onClick={() => onNavigate('config')}
        onMouseEnter={e => { e.currentTarget.querySelectorAll('span').forEach(s => { s.style.color = '#c9a84c'; }); }}
        onMouseLeave={e => { e.currentTarget.querySelectorAll('span').forEach(s => { s.style.color = 'rgba(240,237,230,0.4)'; }); }}>
        <span style={{ fontSize: '14px', color: 'rgba(240,237,230,0.4)' }}>⚙</span>
        <span style={{ fontSize: '13px', color: 'rgba(240,237,230,0.4)', fontFamily: "'Jost',sans-serif" }}>Settings</span>
      </div>

      <div style={{ marginTop: 'auto', paddingTop: '16px', borderTop: `0.5px solid ${V.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 12px', borderRadius: '7px', cursor: 'pointer', marginBottom: '10px',
          background: speech.enabled ? 'rgba(201,168,76,0.08)' : 'transparent',
          border: `0.5px solid ${speech.enabled ? 'rgba(201,168,76,0.25)' : 'transparent'}`,
          boxShadow: speech.enabled ? '0 0 10px rgba(201,168,76,0.1)' : 'none',
        }} onClick={() => { speech.setEnabled(e => !e); if (speech.speaking) speech.stop(); }}>
          <span style={{ fontSize: '14px' }}>{speech.enabled ? '🔊' : '🔇'}</span>
          <span style={{ fontSize: '12px', color: speech.enabled ? V.goldL : V.dim }}>Read Aloud</span>
          {speech.speaking && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: V.gold, marginLeft: 'auto', display: 'inline-block', animation: 'pulse 1s infinite' }} />}
        </div>
        {session && (
          <div style={{ fontSize: '10px', color: V.dim, textAlign: 'center', lineHeight: 1.5, marginBottom: '10px', fontFamily: "'Jost',sans-serif" }}>
            {session.user?.email}
          </div>
        )}
        {session && (
          <>
            <div style={{ borderTop: '0.5px solid rgba(201,168,76,0.12)', marginTop: '4px', marginBottom: '16px' }} />
            <button
              style={{ width: '100%', padding: '10px', background: 'transparent', border: '1px solid rgba(196,122,122,0.35)', borderRadius: '8px', color: V.rose, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '13px', letterSpacing: '0.04em', marginBottom: '10px' }}
              onClick={async () => { await supabase.auth.signOut(); window.location.reload(); }}>
              Sign Out
            </button>
          </>
        )}
        <div style={{ fontSize: '10px', color: V.dim, textAlign: 'center', lineHeight: 1.5 }}>
          © 2025 Trade Like a Woman<br/>All rights reserved.
        </div>
      </div>
    </div>
  );
}

export default Sidebar
